<template>
  <div class="mortgage-connect" dir="rtl">
    <div class="flow-phone-frame">
      <div class="flow-scroll">
        <FlowHeader
          v-if="showHeader"
          class="flow-scroll__header"
          :current-step="headerStep"
          :total-steps="totalSteps"
          :show-back="canGoBack"
          :hide-progress="isErrorStep"
          @back="goBack"
        />
        <div class="flow-content">
          <transition :name="transitionName" mode="out-in">
        <StepBankIntro
          v-if="currentStep === 'bankIntro'"
          key="step-bank-intro"
          @next="onBankIntroNext"
        />
        <StepBankSelection
          v-else-if="currentStep === 'bankSelect'"
          key="step-bank-select"
          @select="onBankSelect"
        />
        <StepBankCredentials
          v-else-if="currentStep === 'bankCreds'"
          key="step-bank-creds"
          :bank="selectedBank"
          @next="onBankCredsNext"
          @mock-mode="openMockCredentialsFlow"
        />
        <StepMockBankCredentials
          v-else-if="currentStep === 'mockBankCreds'"
          key="step-mock-bank-creds"
          :bank="selectedBank"
          :loading="connectingBank"
          @next="onMockCredsNext"
        />
        <StepBankLoading
          v-else-if="currentStep === 'bankLoading'"
          key="step-bank-loading"
        />
        <StepMissingMortgage
          v-else-if="currentStep === 'missingMortgage'"
          key="step-missing-mortgage"
          :upload-found-no-mortgage="uploadFoundNoMortgage"
          :processing="processingPostUpload"
          @connect-account="onMissingMortgageConnect"
          @help-click="onMissingMortgageHelp"
          @upload-success="onMortgageReportUploaded"
        />
        <StepMissingBalance
          v-else-if="currentStep === 'missingBalance'"
          key="step-missing-balance"
          @next="onMissingBalanceNext"
        />
        <StepConnectedAccounts
          v-else-if="currentStep === 'connected'"
          key="step-connected"
          :accounts="connectedAccounts"
          @add-another="onAddAnother"
          @done="onFlowDone"
        />
          <StepConnectionError
            v-else-if="currentStep === 'error'"
            key="step-error"
            variant="connection"
            @retry="onErrorRetry"
            @contact-team="onErrorContactTeam"
          />
          <StepConnectionError
            v-else-if="currentStep === 'genericError'"
            key="step-generic-error"
            variant="generic"
            @retry="onErrorRetry"
            @contact-team="onErrorContactTeam"
          />
          </transition>
        </div>
      </div>

      <!-- Bank overlay sits on top of bank selection -->
      <transition name="overlay-slide">
        <StepBankOverlay
          v-if="showBankOverlay"
          :bank-name="selectedBank.name"
          @continue="onOverlayContinue"
          @close="showBankOverlay = false"
        />
      </transition>

      <!-- Mortgage report help sheet (opened from missingMortgage step) -->
      <transition name="overlay-slide">
        <MortgageReportHelpSheet
          v-if="showMortgageReportHelp"
          @close="showMortgageReportHelp = false"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import ConsentActionsMixin from '@/base-components/onboarding/openBanking/ConsentActionsMixin.vue';
import CredentialsApi from '@/api/CredentialsApi';
import AggregatorApi from '@/api/AggregatorApi';
import AsyncFallbackApi from '@/api/AsyncFallbackApi';
import Segment from '@/Segment';
import DDLogs from '@/DDLogs';
import FlowHeader from './components/FlowHeader.vue';
import StepBankIntro from './components/StepBankIntro.vue';
import StepBankSelection from './components/StepBankSelection.vue';
import StepBankOverlay from './components/StepBankOverlay.vue';
import StepBankCredentials from './components/StepBankCredentials.vue';
import StepMockBankCredentials from './components/StepMockBankCredentials.vue';
import StepBankLoading from './components/StepBankLoading.vue';
import StepConnectedAccounts from './components/StepConnectedAccounts.vue';
import StepConnectionError from './components/StepConnectionError.vue';
import StepMissingMortgage from './components/StepMissingMortgage.vue';
import StepMissingBalance from './components/StepMissingBalance.vue';
import MortgageReportHelpSheet from './components/MortgageReportHelpSheet.vue';
import { TOTAL_STEPS, stepPosition, MORTGAGE_FLOW_ID } from './flowSteps';

// Local ordering used for slide-direction logic in goToStep/goBack. The
// progress bar uses the shared flowSteps module so the bar is continuous
// across the whole mortgage-refinance journey.
const BANK_STEPS = [
  'bankIntro', 'bankSelect', 'bankCreds', 'mockBankCreds', 'bankLoading',
  'missingMortgage', 'missingBalance', 'connected', 'error', 'genericError',
];

const ERROR_STEPS = ['error', 'genericError'];

// 5 minutes — total wait before giving up and routing to missingMortgage.
const MORTGAGE_DATA_TIMEOUT_MS = 5 * 60 * 1000;
// Match the existing PreparingOTS.vue polling cadence.
const MORTGAGE_DATA_POLL_MS = 3 * 1000;

export default {
  name: 'MortgageRefinanceConnect',
  mixins: [ConsentActionsMixin],
  components: {
    FlowHeader,
    StepBankIntro,
    StepBankSelection,
    StepBankOverlay,
    StepBankCredentials,
    StepMockBankCredentials,
    StepBankLoading,
    StepConnectedAccounts,
    StepConnectionError,
    StepMissingMortgage,
    StepMissingBalance,
    MortgageReportHelpSheet,
  },
  data() {
    return {
      currentStep: 'bankIntro',
      // Step that was active before we routed to an error step — used by
      // the error-screen "נסה שוב" CTA to return the customer to where they
      // were instead of restarting the flow.
      stepBeforeError: null,
      transitionDirection: 'forward',
      selectedBank: null,
      showBankOverlay: false,
      connectingBank: false,
      connectedAccounts: [],
      credentialsName: '',
      credentialsIdNumber: '',
      pendingBankCredsData: null,
      pendingMockCredsData: null,
      showMortgageReportHelp: false,
      mortgageDataTimeoutId: null,
      mortgageDataPollId: null,
      mortgageDataDelayId: null,
      // True after a mortgage-report upload landed back on missingMortgage
      // (i.e. the report was accepted but recalc didn't find a mortgage).
      uploadFoundNoMortgage: false,
      pendingUploadCheck: false,
      // While true, we're polling for mortgage data after a file upload
      // without changing the user's screen — they stay on missingMortgage
      // with the upload button still in its loading state.
      processingPostUpload: false,
    };
  },
  beforeDestroy() {
    this.stopMortgageDataWait();
  },
  async created() {
    Segment.trackUserGot('MortgageRefinanceConnect_PageLoaded');

    // Handle return from bank after consent approval
    if (this.$route.query.step === 'bankLoading') {
      Segment.trackUserGot('MortgageRefinanceConnect_ReturnedFromBank');
      this.currentStep = 'bankLoading';
      await this.handleBankReturn();
      return;
    }

    // Restore from backend state on reload — if the customer already connected
    // an account, drop them into the appropriate step instead of the intro.
    await this.restoreFromBackendState();
  },
  computed: {
    ...mapState('credentials', ['isLoading', 'asyncTimeoutReached', 'credentialsValid']),
    ...mapState('onboardingState', ['connectionSuccess', 'saveCredentialsError']),
    ...mapState('mortgages', ['mortgageSimulations', 'mortgageFromCashflow']),
    ...mapGetters('onboardingState', ['invalidCredentials', 'obkConnectionError']),
    ...mapGetters('mortgages', ['hasMortgageAlternatives', 'hasMortgageData']),
    transitionName() {
      return this.transitionDirection === 'forward' ? 'slide-left' : 'slide-right';
    },
    showHeader() {
      return !['bankLoading', 'connected'].includes(this.currentStep);
    },
    totalSteps() {
      return TOTAL_STEPS;
    },
    headerStep() {
      return stepPosition(this.currentStep);
    },
    canGoBack() {
      if (this.currentStep === 'bankIntro') { return false; }
      if (this.currentStep === 'bankLoading') { return false; }
      if (this.currentStep === 'connected') { return false; }
      if (this.isErrorStep) { return false; }
      return true;
    },
    isErrorStep() {
      return ERROR_STEPS.includes(this.currentStep);
    },
  },
  watch: {
    isLoading(newVal, oldVal) {
      if (oldVal && !newVal && this.currentStep === 'bankLoading') {
        DDLogs.log('[mortgage-refinance-connect] Loading finished', {
          credentialsValid: this.credentialsValid,
          saveCredentialsError: this.saveCredentialsError,
        });
        if (this.credentialsValid && !this.saveCredentialsError) {
          this.onBankConnectionSuccess();
        } else if (this.saveCredentialsError || this.obkConnectionError) {
          Segment.trackUserGot('MortgageRefinanceConnect_BankConnectionError');
          this.goToStep('error');
        }
      }
    },
    asyncTimeoutReached(val) {
      if (val && this.currentStep === 'bankLoading') {
        DDLogs.log('[mortgage-refinance-connect] Async timeout reached');
        Segment.trackUserGot('MortgageRefinanceConnect_BankConnectionTimeout', {
          bankId: this.selectedBank?.id,
        });
        this.handleAsyncFallback();
      }
    },
  },
  methods: {
    ...mapActions('credentials', ['fetchLoadingCredentialsState', 'handleLoadingState', 'fetchCredentials', 'saveCredentials']),
    ...mapActions('mortgages', ['fetchMortgageSimulations']),
    ...mapActions('ots', ['isFinishedLoadingCashflow']),
    ...mapMutations('mortgages', ['setMortgageSimulations', 'setMortgageFromCashflow']),
    goToStep(step) {
      const currentIdx = BANK_STEPS.indexOf(this.currentStep);
      const nextIdx = BANK_STEPS.indexOf(step);
      this.transitionDirection = nextIdx > currentIdx ? 'forward' : 'backward';
      // Capture the pre-error step so the retry button can return there.
      if (ERROR_STEPS.includes(step) && !ERROR_STEPS.includes(this.currentStep)) {
        this.stepBeforeError = this.currentStep;
      }
      this.currentStep = step;
    },
    goBack() {
      const idx = BANK_STEPS.indexOf(this.currentStep);
      if (idx > 0) {
        this.transitionDirection = 'backward';
        this.currentStep = BANK_STEPS[idx - 1];
      }
    },
    onBankIntroNext() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_BankIntroContinued');
      this.goToStep('bankSelect');
    },
    async restoreFromBackendState() {
      try {
        const credentialsSettings = await CredentialsApi.fetchCredentialsSettings();
        const bankCredentials = (credentialsSettings.credentialsConfigurations || [])
          .filter(cred => cred.bankIdentifier);
        if (bankCredentials.length === 0) {
          // No accounts connected yet — leave default 'bankIntro'.
          return;
        }
        DDLogs.log('[mortgage-refinance-connect] Restoring from backend state', {
          connectedAccountsCount: bankCredentials.length,
        });
        this.connectedAccounts = bankCredentials.map(cred => ({
          bankName: cred.sourceName,
          bankId: cred.credentialsId,
          accountName: cred.name,
          accountNumber: cred.accounts?.[0]?.accountNumberPiiValue,
        }));
        // If the backend hasn't finished processing the cashflow yet, drop
        // into the loader and let polling do the routing — otherwise we'd
        // route on stale mortgage data from before the new account landed.
        let finishedLoading = false;
        try {
          finishedLoading = await this.isFinishedLoadingCashflow();
        } catch (error) {
          DDLogs.error('[mortgage-refinance-connect] isFinishedLoadingCashflow failed in restore', {
            error: error.message,
          });
        }
        if (!finishedLoading) {
          Segment.trackUserGot('MortgageRefinanceConnect_RestoredToLoading');
          this.currentStep = 'bankLoading';
          this.startMortgageDataWait();
          return;
        }
        // Customer has connected accounts — figure out which step to land on
        // based on mortgage data.
        await this.fetchMortgageSimulations();
        if (this.hasMortgageAlternatives) {
          Segment.trackUserGot('MortgageRefinanceConnect_RestoredToResults');
          this.$router.push({ path: '/ots/mortgage', query: this.$route.query });
          return;
        }
        if (this.hasMortgageData) {
          Segment.trackUserGot('MortgageRefinanceConnect_RestoredToResults');
          this.$router.push({ path: '/ots/mortgage', query: this.$route.query });
          return;
        }
        if (this.mortgageFromCashflow) {
          Segment.trackUserGot('MortgageRefinanceConnect_RestoredToMissingMortgage');
          this.currentStep = 'missingMortgage';
          return;
        }
        if (this.mortgageSimulations) {
          Segment.trackUserGot('MortgageRefinanceConnect_RestoredToMissingMortgage');
          this.currentStep = 'missingMortgage';
          return;
        }
        // Calculation hasn't completed yet — drop into the loader and start
        // the wait again.
        Segment.trackUserGot('MortgageRefinanceConnect_RestoredToLoading');
        this.currentStep = 'bankLoading';
        this.startMortgageDataWait();
      } catch (error) {
        DDLogs.error('[mortgage-refinance-connect] Failed to restore from backend state', {
          error: error.message,
        });
        Segment.trackUserGot('MortgageRefinanceConnect_RestoreFailed', { error: error.message });
        this.goToStep('genericError');
      }
    },
    onBankSelect(bank) {
      this.selectedBank = bank;
      Segment.trackUserInteraction('MortgageRefinanceConnect_BankSelected', { bankId: bank.id, bankName: bank.name });
      this.goToStep('bankCreds');
    },
    onOverlayContinue() {
      this.showBankOverlay = false;
      if (this.pendingMockCredsData) {
        const data = this.pendingMockCredsData;
        this.pendingMockCredsData = null;
        this.submitMockCreds(data);
      } else if (this.pendingBankCredsData) {
        const data = this.pendingBankCredsData;
        this.pendingBankCredsData = null;
        this.submitBankCreds(data);
      }
    },
    openMockCredentialsFlow() {
      Segment.trackUserGot('MortgageRefinanceConnect_MockModeActivated');
      this.goToStep('mockBankCreds');
    },
    onMockCredsNext({ accountName, credentials }) {
      Segment.trackUserInteraction('MortgageRefinanceConnect_MockCredsSubmitted', {
        bankId: this.selectedBank.id,
        bankName: this.selectedBank.name,
      });
      // Stash and show the pre-redirect overlay; submit on overlay continue.
      this.pendingMockCredsData = { accountName, credentials };
      this.showBankOverlay = true;
    },
    async submitMockCreds({ accountName, credentials }) {
      this.connectingBank = true;
      this.credentialsName = accountName;
      try {
        await this.saveCredentials({
          sourceName: this.selectedBank.sourceName,
          credentials,
          name: accountName,
          provider: this.selectedBank.sourceName,
        });
        this.connectingBank = false;
        this.goToStep('bankLoading');
      } catch (error) {
        this.connectingBank = false;
        DDLogs.error('[mortgage-refinance-connect] Mock credentials save error', { error: error.message });
        Segment.trackUserGot('MortgageRefinanceConnect_MockCredsError', {
          bankId: this.selectedBank.id,
          error: error.message,
        });
        this.goToStep('error');
      }
    },
    onBankCredsNext(data) {
      Segment.trackUserInteraction('MortgageRefinanceConnect_BankCredsSubmitted', {
        bankId: this.selectedBank.id,
        bankName: this.selectedBank.name,
      });
      // Stash the data and surface the pre-redirect overlay. The actual
      // submit + bank redirect happens in submitBankCreds when the customer
      // confirms via the overlay's CTA.
      this.pendingBankCredsData = data;
      this.showBankOverlay = true;
    },
    async submitBankCreds(data) {
      this.connectingBank = true;
      this.credentialsName = data.accountName;
      this.credentialsIdNumber = data.idNumber;

      try {
        const { credentialsConsent } = await AggregatorApi.getCredentialsConsent({
          bankIdentifier: this.selectedBank.openBanking.aggregatorBankId,
          userIdInBank: data.idNumber,
        });

        const redirectParams = {
          idNumber: data.idNumber,
          source: {
            sourceName: this.selectedBank.sourceName,
            openBanking: this.selectedBank.openBanking,
            type: 'bank',
          },
          name: data.accountName,
          mortgageFlowId: MORTGAGE_FLOW_ID,
        };

        if (credentialsConsent) {
          Segment.trackUserGot('MortgageRefinanceConnect_ExistingConsentFound', {
            bankId: this.selectedBank.id,
            consentId: credentialsConsent.consentId,
          });
          redirectParams.credentialsToUpdateMetaData = {
            consentId: credentialsConsent.consentId,
            credentialsId: credentialsConsent.credentialsId,
          };
        }

        await this.redirectToGetAccessCode(redirectParams);
      } catch (error) {
        this.connectingBank = false;
        DDLogs.error('[mortgage-refinance-connect] Bank connection error', { error: error.message });
        Segment.trackUserGot('MortgageRefinanceConnect_BankConnectionError', {
          bankId: this.selectedBank.id,
          error: error.message,
        });
        this.goToStep('error');
      }
    },
    onErrorRetry() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_ErrorRetry');
      // Return the customer to the step they were on before the error. If
      // we don't have one captured (or it would render blank), pick a safe
      // fallback based on whether a bank was selected.
      const target = this.stepBeforeError
        || (this.selectedBank ? 'bankCreds' : 'bankSelect');
      this.stepBeforeError = null;
      this.goToStep(target);
      // Re-arm the mortgage-data poll if the customer was waiting on it.
      if (target === 'bankLoading') {
        this.startMortgageDataWait();
      }
    },
    onErrorContactTeam() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_ErrorContactTeam');
      // TODO: wire up the "talk to team" path once product confirms the
      // channel (likely Intercom or a support route).
    },
    async onBankConnectionSuccess() {
      try {
        const credentialsSettings = await CredentialsApi.fetchCredentialsSettings();
        const credentials = credentialsSettings.credentialsConfigurations || [];
        this.connectedAccounts = credentials
          .filter(cred => cred.bankIdentifier)
          .map(cred => ({
            bankName: cred.sourceName,
            bankId: cred.credentialsId,
            accountName: cred.name,
            accountNumber: cred.accounts?.[0]?.accountNumberPiiValue,
          }));
        Segment.trackUserGot('MortgageRefinanceConnect_BankConnectionSuccess', {
          connectedAccountsCount: this.connectedAccounts.length,
        });
      } catch (error) {
        DDLogs.error('[mortgage-refinance-connect] FetchCredentialsError', { error: error.message });
        Segment.trackUserGot('MortgageRefinanceConnect_FetchCredentialsError', { error: error.message });
      }
      // Stay on bankLoading and wait for the backend to finish calculating
      // mortgage data. The websocket watcher (or the timeout fallback) will
      // route us to the next step once data is ready.
      this.startMortgageDataWait();
    },
    startMortgageDataWait({ initialDelayMs = 0 } = {}) {
      DDLogs.log('[mortgage-refinance-connect] Waiting for mortgage data', { initialDelayMs });
      Segment.trackUserGot('MortgageRefinanceConnect_WaitingForMortgageData');
      // Cancel any prior wait so we never end up with two parallel poll chains.
      this.stopMortgageDataWait();
      // Clear any leftover mortgage state from earlier sessions so we don't
      // route prematurely on the first poll.
      this.setMortgageSimulations(undefined);
      this.setMortgageFromCashflow(undefined);
      // Reset the stop sentinel so the new chain can schedule.
      this.mortgageDataPollId = null;
      // Use a self-rescheduling chain (instead of setInterval) so we never
      // start a new fetch while the previous one is still in flight on a
      // slow network — overlapping polls cause racey routing decisions.
      const scheduleNext = () => {
        if (this.mortgageDataPollId === 'stopped') { return; }
        this.mortgageDataPollId = setTimeout(async () => {
          await this.checkMortgageDataAndRoute();
          scheduleNext();
        }, MORTGAGE_DATA_POLL_MS);
      };
      const startPolling = async () => {
        await this.checkMortgageDataAndRoute();
        scheduleNext();
      };
      if (initialDelayMs > 0) {
        // Give the backend time to start recalculating before first poll —
        // otherwise we'd see the stale data and route immediately.
        this.mortgageDataDelayId = setTimeout(startPolling, initialDelayMs);
      } else {
        startPolling();
      }
      this.mortgageDataTimeoutId = setTimeout(() => {
        if (this.currentStep === 'bankLoading' || this.processingPostUpload) {
          DDLogs.log('[mortgage-refinance-connect] Mortgage data wait timed out');
          Segment.trackUserGot('MortgageRefinanceConnect_MortgageDataTimeout');
          this.stopMortgageDataWait();
          this.pendingUploadCheck = false;
          this.processingPostUpload = false;
          // Backend never returned mortgage data within the wait window —
          // most likely an outage or stuck job. Show the generic error so
          // the customer can retry or contact support, rather than the
          // misleading "no mortgage found" copy.
          this.goToStep('genericError');
        }
      }, MORTGAGE_DATA_TIMEOUT_MS);
    },
    stopMortgageDataWait() {
      if (this.mortgageDataDelayId) {
        clearTimeout(this.mortgageDataDelayId);
        this.mortgageDataDelayId = null;
      }
      if (this.mortgageDataPollId && this.mortgageDataPollId !== 'stopped') {
        clearTimeout(this.mortgageDataPollId);
      }
      // Sentinel value prevents an in-flight checkMortgageDataAndRoute from
      // scheduling another tick after we stop.
      this.mortgageDataPollId = 'stopped';
      if (this.mortgageDataTimeoutId) {
        clearTimeout(this.mortgageDataTimeoutId);
        this.mortgageDataTimeoutId = null;
      }
    },
    async checkMortgageDataAndRoute() {
      // Allow polling on bankLoading OR while doing a post-upload re-check
      // (in which case we stay on missingMortgage with the button spinner).
      if (this.currentStep !== 'bankLoading' && !this.processingPostUpload) { return; }
      // Gate on the backend's "cashflow finished loading" signal — otherwise
      // we'd route on stale mortgage data from before the new account was
      // processed (e.g. mortgageFromCashflow=true left over from before the
      // bank account was connected).
      try {
        const finishedLoading = await this.isFinishedLoadingCashflow();
        if (!finishedLoading) { return; }
      } catch (error) {
        DDLogs.error('[mortgage-refinance-connect] isFinishedLoadingCashflow failed', { error: error.message });
        return;
      }
      try {
        await this.fetchMortgageSimulations();
      } catch (error) {
        DDLogs.error('[mortgage-refinance-connect] fetchMortgageSimulations failed', { error: error.message });
        return;
      }
      if (this.hasMortgageAlternatives) {
        this.stopMortgageDataWait();
        this.pendingUploadCheck = false;
        this.processingPostUpload = false;
        Segment.trackUserGot('MortgageRefinanceConnect_MortgageAlternativesFound');
        this.$router.push({ path: '/ots/mortgage', query: this.$route.query });
        return;
      }
      // Real mortgage data exists but no qualifying alternatives (e.g.
      // largeMonthlyCashflowDiff or no qualifying KPI). Hand off to
      // /ots/mortgage so the customer sees NoMortgageAlternativesPage with
      // their actual mortgage info.
      if (this.hasMortgageData) {
        this.stopMortgageDataWait();
        this.pendingUploadCheck = false;
        this.processingPostUpload = false;
        Segment.trackUserGot('MortgageRefinanceConnect_MortgageDataNoAlternatives');
        this.$router.push({ path: '/ots/mortgage', query: this.$route.query });
        return;
      }
      // Backend inferred a mortgage from cashflow (no real mortgage account
      // data) — we don't have enough to show alternatives. Route to the
      // missingMortgage step in-flow so the customer can upload a report or
      // connect a different account.
      if (this.mortgageFromCashflow) {
        this.stopMortgageDataWait();
        this.pendingUploadCheck = false;
        this.processingPostUpload = false;
        Segment.trackUserGot('MortgageRefinanceConnect_MortgageFromCashflow');
        if (this.currentStep !== 'missingMortgage') {
          this.goToStep('missingMortgage');
        }
        return;
      }
      // Calculation finished but no mortgage was found.
      // Mirrors /mortgage's <no-mortgage-page v-else /> fallback.
      if (this.mortgageSimulations) {
        this.stopMortgageDataWait();
        if (this.pendingUploadCheck) {
          this.uploadFoundNoMortgage = true;
          this.pendingUploadCheck = false;
          Segment.trackUserGot('MortgageRefinanceConnect_UploadFoundNoMortgage');
        } else {
          Segment.trackUserGot('MortgageRefinanceConnect_NoMortgageFound');
        }
        this.processingPostUpload = false;
        // Already on missingMortgage during post-upload — no need to navigate.
        if (this.currentStep !== 'missingMortgage') {
          this.goToStep('missingMortgage');
        }
      }
      // Otherwise: data hasn't arrived yet, keep polling.
    },
    onMissingMortgageConnect() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_MissingMortgage_ConnectClicked');
      this.selectedBank = null;
      this.uploadFoundNoMortgage = false;
      this.goToStep('bankSelect');
    },
    onMissingMortgageHelp() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_MissingMortgage_HelpClicked');
      this.showMortgageReportHelp = true;
    },
    onMortgageReportUploaded() {
      Segment.trackUserGot('MortgageRefinanceConnect_MortgageReportUploaded');
      // Mark the next mortgage-data wait as the post-upload one, so if we
      // end up still on missingMortgage we can tell the user the report was
      // processed but no mortgage was detected.
      this.pendingUploadCheck = true;
      this.uploadFoundNoMortgage = false;
      // Stay on missingMortgage and keep the upload button in its loading
      // state while we poll the backend in the background. Delay the first
      // poll so the backend has time to start recalculating.
      this.processingPostUpload = true;
      this.startMortgageDataWait({ initialDelayMs: 8000 });
    },
    onMissingBalanceNext() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_MissingBalance_NextClicked');
      this.selectedBank = null;
      this.goToStep('bankSelect');
    },
    onAddAnother() {
      this.selectedBank = null;
      Segment.trackUserInteraction('MortgageRefinanceConnect_AddAnotherBank');
      this.goToStep('bankSelect');
    },
    onFlowDone() {
      Segment.trackUserInteraction('MortgageRefinanceConnect_FlowCompleted', {
        connectedAccountsCount: this.connectedAccounts.length,
      });
    },
    async handleBankReturn() {
      try {
        DDLogs.log('[mortgage-refinance-connect] Handling bank return');
        // Fetch the current loading state from the backend
        await this.fetchLoadingCredentialsState();

        if (this.isLoading) {
          // Still loading - start the timeout countdown
          DDLogs.log('[mortgage-refinance-connect] Credentials still loading, starting timeout');
          await this.handleLoadingState();
          // The watchers will handle the state changes
        } else if (this.credentialsValid) {
          // Already finished loading successfully
          DDLogs.log('[mortgage-refinance-connect] Credentials already loaded');
          await this.onBankConnectionSuccess();
        } else {
          // Loading finished but failed
          DDLogs.log('[mortgage-refinance-connect] Credentials loading failed');
          Segment.trackUserGot('MortgageRefinanceConnect_BankConnectionError');
          this.goToStep('error');
        }
      } catch (error) {
        DDLogs.error('[mortgage-refinance-connect] HandleBankReturnError', { error: error.message });
        Segment.trackUserGot('MortgageRefinanceConnect_HandleBankReturnError', { error: error.message });
      }
    },
    async handleAsyncFallback() {
      try {
        // Notify backend that we hit the timeout
        await AsyncFallbackApi.publishAsyncFallback();
        DDLogs.log('[mortgage-refinance-connect] Async fallback published');
        Segment.trackUserGot('MortgageRefinanceConnect_AsyncFallbackPublished');

        // For now, still try to fetch credentials - they might be ready
        await this.fetchCredentials();
        const credentialsSettings = await CredentialsApi.fetchCredentialsSettings();
        const credentials = credentialsSettings.credentialsConfigurations || [];
        const bankCredentials = credentials.filter(cred => cred.bankIdentifier);

        if (bankCredentials.length > 0) {
          // Credentials are actually ready, proceed to connected
          this.connectedAccounts = bankCredentials.map(cred => ({
            bankName: cred.sourceName,
            bankId: cred.credentialsId,
            accountName: cred.name,
            accountNumber: cred.accounts?.[0]?.accountNumberPiiValue,
          }));
          this.goToStep('connected');
        } else {
          // Async fallback fired but no credentials landed — treat as a
          // failed connection rather than pretending it succeeded.
          DDLogs.log('[mortgage-refinance-connect] No credentials after async timeout');
          Segment.trackUserGot('MortgageRefinanceConnect_AsyncFallbackNoCredentials');
          this.goToStep('genericError');
        }
      } catch (error) {
        DDLogs.error('[mortgage-refinance-connect] AsyncFallbackError', { error: error.message });
        Segment.trackUserGot('MortgageRefinanceConnect_AsyncFallbackError', { error: error.message });
        this.goToStep('genericError');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mortgage-connect {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100dvh;
  // Page letterbox color shown around the phone-sized frame on desktop.
  background: #EAE9E4;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.flow-phone-frame {
  position: relative;
  width: 100%;
  height: 100%;
  background: #f9f7ed;
  // Internal padding creates the cream gap between the frame's outer
  // edge and the green border line drawn on .flow-frame-inner.
  // 13px top, 10px sides — matches Figma frame (523:161). Bottom is 0
  // so the bordered scroll area extends to the frame's bottom edge;
  // the rounded green bottom corners appear when content scrolls to
  // the end of the list, not as fixed chrome.
  padding: 13px 10px 0;
  box-sizing: border-box;

  // Cap to phone-portrait dimensions ONLY on wider (desktop) viewports
  // so the frame looks like a phone screen instead of a giant rectangle.
  @media (min-width: 500px) {
    max-width: 402px;
    max-height: 874px;
  }
}

.flow-scroll {
  height: 100%;
  overflow-y: auto;
  // 13px scrollable padding below .flow-content so the rounded bottom
  // corners sit 13px above the frame's bottom at end-of-scroll.
  padding-bottom: 13px;
  box-sizing: border-box;
  // Match the header's rounded top corners so scrolling content cannot
  // paint into the corner-cutout area (which would expose .flow-content's
  // side borders behind the header).
  border-radius: 25px 25px 0 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  // Column flex so .flow-content's min-height: 100% means "the rest of
  // the frame after the sticky header" instead of "the full frame" —
  // otherwise short steps push the bottom border past the frame and
  // force scroll on screens that should fit.
  display: flex;
  flex-direction: column;
  &::-webkit-scrollbar { display: none; }
}

// Page-level border: left, right, bottom + rounded bottom corners.
// Top is open (no top border, no top corners). Scrolls with content
// when the step is taller than the available space (the bordered area
// is "an object that scrolls with the list", not fixed chrome) — the
// rounded bottom corners appear at end-of-scroll.
.flow-content {
  flex: 1 0 auto;
  border-left: 1px solid rgba(32, 84, 65, 0.2);
  border-right: 1px solid rgba(32, 84, 65, 0.2);
  border-bottom: 1px solid rgba(32, 84, 65, 0.2);
  border-radius: 0 0 25px 25px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

// Sticky header sitting OUTSIDE .flow-content (sibling of it within
// .flow-scroll). Draws the TOP arc: top + side borders + rounded top
// corners. Side borders align with .flow-content's side borders below
// so the side line reads as one continuous edge.
.flow-scroll__header {
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
  background: #f9f7ed;
  border-top: 1px solid rgba(32, 84, 65, 0.2);
  border-left: 1px solid rgba(32, 84, 65, 0.2);
  border-right: 1px solid rgba(32, 84, 65, 0.2);
  border-radius: 25px 25px 0 0;
  box-sizing: border-box;
}

// Step transitions
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.25s ease-out, opacity 0.25s ease-out;
}

.slide-left-enter {
  transform: translateX(-12px);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(12px);
  opacity: 0;
}

.slide-right-enter {
  transform: translateX(12px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(-12px);
  opacity: 0;
}

// Overlay transitions
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter,
.overlay-fade-leave-to {
  opacity: 0;
}
</style>
